import React from "react";
import { itemsApi, ordersApi } from "../api/client";
import { formatTimer } from "@shared/utils/time";
import { formatOrderSource, formatDailyNumber } from "@shared/utils/format";
import { waveLabel } from "@shared/utils/wave";
import { useState, useEffect, useCallback } from "react";
import {
  Clock,
  Flame,
  ChevronRight,
  CheckCircle2,
  X,
  AlertTriangle,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useKitchenStore } from "../stores/kitchen.store";

interface OrderCardProps {
  order: any;
  compact?: boolean;
  stationId?: string;
}

// Status cycle for tap-to-advance behaviour on item rows.
// Chef's last responsibility is READY — after that the waiter takes over and
// marks SERVED on pickup/delivery, so the chef UI must NOT advance to SERVED.
const STATUS_CYCLE: Record<string, string> = {
  PENDING: "PREPARING",
  PREPARING: "READY",
};

// Coloured dot indicator for item status
const STATUS_DOT_CLASS: Record<string, string> = {
  PENDING: "bg-kds-pending",
  CONFIRMED: "bg-kds-pending",
  IN_PROGRESS: "bg-kds-preparing",
  PREPARING: "bg-kds-preparing",
  READY: "bg-kds-ready",
  SERVED: "bg-kds-served",
  COMPLETED: "bg-kds-served",
  CANCELLED: "bg-kds-cancelled",
};

// Human-readable label shown next to the dot
const STATUS_LABEL: Record<string, string> = {
  PENDING: "Da fare",
  CONFIRMED: "Da fare",
  IN_PROGRESS: "In prep.",
  PREPARING: "In prep.",
  READY: "Pronto",
  SERVED: "Servito",
  COMPLETED: "Completato",
  CANCELLED: "Annullato",
};

// Card border colour based on order status
const CARD_BORDER_CLASS: Record<string, string> = {
  PENDING: "border-kds-pending",
  CONFIRMED: "border-kds-pending",
  IN_PROGRESS: "border-kds-preparing",
  READY: "border-kds-ready",
  SERVED: "border-kds-served",
  COMPLETED: "border-kds-served",
  CANCELLED: "border-kds-cancelled",
};

export default function OrderCard({ order, compact, stationId }: OrderCardProps) {
  const queryClient = useQueryClient();
  const dishInventory = useKitchenStore((s) => s.dishInventory);

  // When rendered inside a station column, only show items for that station.
  // ESCLUDE gli item CANCELLED dalla vista chef (feedback Mario Nereide
  // 2026-05-29): il cuoco non deve vedere righe annullate, sono distraenti
  // e portano errori (es. preparare un piatto che è stato cancellato).
  const visibleItems: any[] = (
    stationId
      ? (order.items ?? []).filter((i: any) => i.stationId === stationId)
      : (order.items ?? [])
  ).filter((i: any) => i.status !== "CANCELLED");
  const [now, setNow] = useState(() => Date.now());
  // Track which item ids are currently being mutated to prevent double-taps
  const [loadingItemIds, setLoadingItemIds] = useState<Set<string>>(new Set());
  const [loadingAction, setLoadingAction] = useState<
    "fire" | "complete" | null
  >(null);
  // Modal di conferma cancellazione ordine. Usiamo un dialog disruptive
  // (NON window.confirm) perche' lo chef in cucina cliccava la X pensando
  // fosse un "fatto/avanti": ora la X apre un modal rosso esplicito che
  // elenca le conseguenze dell'azione.
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelInFlight, setCancelInFlight] = useState(false);
  // 2026-05-24 fix #2: il tap sulla riga item DEVE aprire il modal di
  // dettaglio (nome + variante + modifiche + note + qty + status), NON
  // cambiare lo stato. Il cambio status resta esclusivo del bottone
  // "▶ Inizia" / "🔔 PRONTO" (modalità compact). Prima il <div> esterno
  // aveva onClick={handleItemTap} e il tap sul testo cambiava status
  // accidentalmente — bug riportato dall'utente.
  const [detailItem, setDetailItem] = useState<any | null>(null);

  // Single ticker drives every per-wave timer on the card.
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // --- Per-wave fire timestamps ---------------------------------------
  // La chef card mostra un timer SOLO per le wave già "firate" (cioè
  // mandate in cucina). Le wave successive restano in "In attesa" finché
  // il cameriere non preme "Invia prossima portata".
  // Anche l'indicatore in alto segue la wave attiva, non più createdAt.
  const waveFiredAt = React.useMemo(() => {
    const map = new Map<number, Date | null>();
    for (const i of visibleItems) {
      const w = i.serviceWave ?? i.courseNumber ?? 1;
      const fired = i.firedAt ? new Date(i.firedAt) : null;
      const existing = map.get(w);
      if (fired && (!existing || fired < existing)) map.set(w, fired);
      else if (!existing) map.set(w, null);
    }
    return map;
  }, [visibleItems]);

  // The wave whose timer represents "currently cooking". Highest wave
  // that has firedAt set AND at least one item still in progress
  // (PENDING/CONFIRMED/PREPARING/IN_PROGRESS).
  //
  // Ritorna `null` quando ogni wave firata ha tutti gli item READY/SERVED:
  // in quel caso lo chef ha finito e la card non deve più ticchettare né
  // pulsare — il piatto passa in mano al cameriere, che ha il suo timer
  // di ritiro.
  const activeWave = React.useMemo(() => {
    const waves = [...waveFiredAt.entries()]
      .filter(([, fa]) => fa !== null)
      .map(([w]) => w)
      .sort((a, b) => b - a); // descending
    for (const w of waves) {
      const hasInProgress = visibleItems.some((i: any) => {
        const iw = i.serviceWave ?? i.courseNumber ?? 1;
        return (
          iw === w &&
          i.status !== "READY" &&
          i.status !== "SERVED" &&
          i.status !== "CANCELLED"
        );
      });
      if (hasInProgress) return w;
    }
    return null;
  }, [waveFiredAt, visibleItems]);

  const activeWaveFiredAt =
    activeWave != null ? waveFiredAt.get(activeWave) : null;
  const elapsed = activeWaveFiredAt
    ? Math.floor((now - activeWaveFiredAt.getTime()) / 1000)
    : 0;

  // Invalidate all kitchen order query variants after any mutation
  const invalidateOrders = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["kitchen-orders"] });
    queryClient.invalidateQueries({ queryKey: ["kitchen-station-orders"] });
  }, [queryClient]);

  // Tap an item row to advance its status along the cycle
  async function handleItemTap(item: any) {
    const nextStatus = STATUS_CYCLE[item.status];
    if (!nextStatus) return; // SERVED / CANCELLED — no further transition
    if (loadingItemIds.has(item.id)) return; // already in-flight

    setLoadingItemIds((prev) => new Set(prev).add(item.id));
    try {
      if (item.status === "PREPARING") {
        // PREPARING → READY uses the bump endpoint
        await itemsApi.bump(item.id);
      } else {
        await itemsApi.updateStatus(item.id, nextStatus);
      }
      invalidateOrders();
    } finally {
      setLoadingItemIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }

  async function handleFireCourse() {
    if (loadingAction) return;
    setLoadingAction("fire");
    try {
      await ordersApi.fireCourse(order.id);
      invalidateOrders();
    } finally {
      setLoadingAction(null);
    }
  }

  // Chef shortcut: mark every PREPARING/PENDING item on this card READY at
  // once, so the entire course (or entire card) can be flagged "pronto" with
  // a single tap. Items already READY/SERVED/CANCELLED are left alone.
  async function handleMarkAllReady() {
    if (loadingAction) return;
    // Solo item già firati: quelli "In attesa" non sono di competenza
    // dello chef finché il cameriere non invia la loro portata.
    const targets = visibleItems.filter(
      (i: any) =>
        !!i.firedAt &&
        (i.status === "PREPARING" ||
          i.status === "IN_PROGRESS" ||
          i.status === "PENDING" ||
          i.status === "CONFIRMED"),
    );
    if (targets.length === 0) return;
    setLoadingAction("complete");
    try {
      await Promise.all(
        targets.map((i: any) => {
          if (i.status === "PREPARING" || i.status === "IN_PROGRESS") {
            return itemsApi.bump(i.id);
          }
          // PENDING → jump straight to READY via updateStatus
          return itemsApi.updateStatus(i.id, "READY");
        }),
      );
      invalidateOrders();
    } finally {
      setLoadingAction(null);
    }
  }

  async function handleComplete() {
    if (loadingAction) return;
    setLoadingAction("complete");
    try {
      await ordersApi.complete(order.id);
      invalidateOrders();
    } finally {
      setLoadingAction(null);
    }
  }

  const elapsedMins = Math.floor(elapsed / 60);
  const isOverdue =
    order.estimatedPrepMinutes && elapsedMins > order.estimatedPrepMinutes;
  const isWarning =
    order.estimatedPrepMinutes &&
    elapsedMins > order.estimatedPrepMinutes * 0.75;

  const cardBorder =
    CARD_BORDER_CLASS[order.status] || "border-kds-muted";

  // Determine whether the "Invia Onda" button should be shown.
  // Only show it when there are actual unfired items for the next wave.
  const currentWave = order.currentWave ?? order.currentCourse ?? 1;
  const nextWave = currentWave + 1;
  const hasUnfiredWave = visibleItems.some(
    (i: any) => (i.serviceWave ?? i.courseNumber) === nextWave && !i.firedAt && i.status !== "CANCELLED",
  );

  // Show "Completa" when every non-cancelled item is READY or SERVED
  const activeItems: any[] = visibleItems.filter(
    (i: any) => i.status !== "CANCELLED",
  );
  const allItemsDone =
    activeItems.length > 0 &&
    activeItems.every(
      (i: any) => i.status === "READY" || i.status === "SERVED",
    );

  const showFireCourse = !compact && hasUnfiredWave;
  const showComplete = !compact && allItemsDone;

  // Card pulsa quando c'è un timer attivo (wave firata con almeno un item
  // ancora in prep). Niente pulsazione su card con solo wave "In attesa" o
  // ordini già tutti READY/SERVED — così lo chef vede subito quali ordini
  // stanno cuocendo ora.
  const isTimerRunning = activeWaveFiredAt !== null && activeWave !== null;

  return (
    <div
      className={`bg-kds-surface rounded-xl border-l-4 ${cardBorder} p-4 ${
        order.priority > 0 ? "ring-2 ring-kds-priority" : ""
      } ${isTimerRunning ? "animate-pulse-card" : ""}`}
    >
      {/* Header — table name as primary title; dailyNumber demoted to secondary.
          FR-003: table name is the main heading; daily number is reference only. */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* Table name (primary heading) */}
          <span className="text-order-num text-kds-text font-extrabold">
            {order.tableName
              ? order.tableName
              : order.tableNumber
                ? `Tavolo ${order.tableNumber}`
                : "Asporto"}
          </span>
          {/* Daily number demoted to a small reference badge */}
          <span className="text-xs text-kds-muted bg-kds-card px-1.5 py-0.5 rounded font-mono">
            #{formatDailyNumber(order.dailyNumber)}
          </span>
          {order.priority > 0 && (
            <Flame size={20} className="text-kds-priority" />
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeWaveFiredAt && (
            <span
              className={`text-sm font-mono ${
                isOverdue
                  ? "text-kds-cancelled"
                  : isWarning
                    ? "text-kds-preparing"
                    : "text-kds-muted"
              }`}
              title="Tempo della portata in preparazione"
            >
              <Clock size={14} className="inline mr-1" />
              {formatTimer(elapsed)}
            </span>
          )}
          {/* Cancellazione ordine orfano: bottone X piccolo, conferma modal,
              chiama PUT /orders/:id status=CANCELLED. Visibile su ENTRAMBE
              le viste (chef compact + station full) per permettere all'utente
              di rimuovere ordini stuck senza dover passare dal DB.
              Nascosto solo se l'ordine e' gia' COMPLETED o CANCELLED. */}
          {order.status !== "COMPLETED" && order.status !== "CANCELLED" && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCancelConfirmOpen(true);
              }}
              className="text-red-400 hover:text-white hover:bg-red-600 transition-colors p-1.5 rounded border border-red-400/40 hover:border-red-600"
              title="ANNULLA ordine (apre conferma)"
              aria-label="Annulla ordine"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Meta */}
      <div className="flex items-center gap-2 text-xs text-kds-muted mb-3">
        <span className="bg-kds-card px-2 py-0.5 rounded">
          {formatOrderSource(order.source)}
        </span>
        {order.customerName && <span>{order.customerName}</span>}
      </div>

      {/* Items — grouped by service wave, each row is tappable to advance
          its status. Espandiamo gli item con quantity > 1 in righe singole:
          lo chef cucina N porzioni e deve vederle separate, non "2x cheesecake"
          raggruppato. L'azione di status resta sul vero item.id (1 KitchenOrderItem
          nel DB), quindi marcare 1 riga come ready le marca tutte. */}
      <div className="space-y-2">
        {(() => {
          const expandedItems = visibleItems.flatMap((item: any) => {
            const n = Math.max(1, Number(item.quantity) || 1);
            return Array.from({ length: n }, (_, idx) => ({
              ...item,
              _virtualKey: n > 1 ? `${item.id}-${idx}` : item.id,
            }));
          });
          // Ordine FISSO: il cuoco tocca le righe per cambiare stato e la
          // posizione NON deve mai cambiare. Si ordina per ondata e poi per
          // chiavi immutabili (createdAt → id), MAI per stato: così un refetch
          // dopo il cambio stato non riordina mai le righe sotto il dito.
          const sortedItems = expandedItems.sort((a: any, b: any) => {
            const wa = a.serviceWave ?? a.courseNumber ?? 1;
            const wb = b.serviceWave ?? b.courseNumber ?? 1;
            if (wa !== wb) return wa - wb;
            const ta = new Date(a.createdAt ?? 0).getTime();
            const tb = new Date(b.createdAt ?? 0).getTime();
            if (ta !== tb) return ta - tb;
            return String(a._virtualKey ?? a.id).localeCompare(
              String(b._virtualKey ?? b.id),
            );
          });

          return sortedItems.map((item: any, index: number) => {
            const itemWave = item.serviceWave ?? item.courseNumber ?? 1;
            const prevWave =
              index > 0
                ? (sortedItems[index - 1].serviceWave ??
                  sortedItems[index - 1].courseNumber ??
                  1)
                : 0;
            const showWaveDivider = itemWave !== prevWave;

            const canAdvance = item.status in STATUS_CYCLE;
            const isLoading = loadingItemIds.has(item.id);
            const dotClass = STATUS_DOT_CLASS[item.status] || "bg-kds-muted";
            const label = STATUS_LABEL[item.status] || item.status;

            const thisWaveFiredAt = waveFiredAt.get(itemWave) ?? null;
            const isActive = activeWave === itemWave;
            // Quando la wave è attiva (fired + almeno un item in progress)
            // il timer tickketta. Quando è firata ma tutti gli items sono
            // READY/SERVED, mostriamo un label statico "Pronta". Quando
            // non è ancora firata, mostriamo "In attesa".
            const thisWaveIsDone =
              thisWaveFiredAt !== null &&
              !isActive;
            const thisWaveSecs =
              isActive && thisWaveFiredAt
                ? Math.floor((now - thisWaveFiredAt.getTime()) / 1000)
                : null;

            return (
              <React.Fragment key={item._virtualKey ?? item.id}>
                {showWaveDivider && (
                  <div className="flex items-center gap-2 py-1">
                    <div className="h-px flex-1 bg-kds-muted/30" />
                    <span className="text-xs text-kds-muted font-semibold">
                      {waveLabel(itemWave)}
                    </span>
                    {thisWaveSecs !== null ? (
                      <span className="text-xs font-mono font-bold text-kds-preparing">
                        {formatTimer(thisWaveSecs)}
                      </span>
                    ) : thisWaveIsDone ? (
                      <span className="text-[10px] uppercase tracking-wider text-kds-ready font-bold">
                        Pronta
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-kds-muted italic">
                        In attesa
                      </span>
                    )}
                    <div className="h-px flex-1 bg-kds-muted/30" />
                  </div>
                )}
                <div
                  onClick={() => setDetailItem(item)}
                  className={[
                    "flex items-center justify-between py-2 px-3 rounded-lg transition-colors cursor-pointer hover:brightness-110 active:scale-[0.99]",
                    item.status === "PREPARING"
                      ? "bg-kds-preparing/10 animate-preparing"
                      : item.status === "READY"
                        ? "bg-kds-ready/10"
                        : item.status === "SERVED"
                          ? "bg-kds-served/10 opacity-50"
                          : "bg-kds-card/50",
                    isLoading ? "opacity-60 cursor-wait" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  title="Tocca per vedere i dettagli del piatto"
                >
                  {/* Dish info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-dish-name text-kds-text">
                        {item.dish?.name ?? "—"}
                      </span>
                      {(() => {
                        const stock = item.dish?.id ? dishInventory[item.dish.id] : null;
                        if (!stock) return null;
                        if (stock.isOutOfStock) {
                          return (
                            <span
                              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: "#EF4444" }}
                              title={`Esaurito (scorte: ${stock.currentStock})`}
                            />
                          );
                        }
                        if (stock.isLowStock) {
                          return (
                            <span
                              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: "#F97316" }}
                              title={`Scorte basse (${stock.currentStock} rimasti)`}
                            />
                          );
                        }
                        return null;
                      })()}
                    </div>
                    {item.modifications && (
                      <p className="text-dish-note text-kds-preparing mt-0.5">
                        &#x26A0; {item.modifications}
                      </p>
                    )}
                    {item.notes && (
                      <p className="text-dish-note text-kds-muted mt-0.5">
                        {item.notes}
                      </p>
                    )}
                  </div>

                  {/* Status indicator + tap hint (station / full mode) */}
                  {!compact && (
                    <div className="ml-3 flex items-center gap-2 shrink-0">
                      <span
                        className={`inline-block w-3 h-3 rounded-full ${dotClass}`}
                        title={label}
                      />
                      <span className="text-xs text-kds-muted hidden sm:inline">
                        {label}
                      </span>
                      {canAdvance && !isLoading && (
                        <ChevronRight
                          size={16}
                          className="text-kds-muted opacity-60"
                        />
                      )}
                      {isLoading && (
                        <span className="w-4 h-4 border-2 border-kds-muted border-t-transparent rounded-full animate-spin" />
                      )}
                    </div>
                  )}

                  {/* Chef overview (compact): one big explicit action button
                      per item. No silent cycle, no SERVED from chef. */}
                  {compact && (
                    <div className="ml-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                      {/* Il pulsante "Inizia" appare SOLO quando il cameriere
                          ha effettivamente firato la portata (firedAt !==
                          null). Fino ad allora il piatto è "In attesa" e
                          non deve essere lavorabile dallo chef. */}
                      {(item.status === "PENDING" || item.status === "CONFIRMED") && item.firedAt ? (
                        <button
                          onClick={() => handleItemTap(item)}
                          disabled={isLoading}
                          className="px-3 py-2 rounded-lg bg-kds-card text-kds-text text-sm font-semibold hover:bg-kds-card/70 disabled:opacity-50 whitespace-nowrap"
                          title="Inizia la preparazione di questo piatto"
                        >
                          {isLoading ? "…" : "▶ Inizia"}
                        </button>
                      ) : (item.status === "PENDING" || item.status === "CONFIRMED") && !item.firedAt ? (
                        <span className="px-3 py-2 rounded-lg bg-kds-card/30 text-kds-muted text-xs font-semibold whitespace-nowrap italic">
                          In attesa
                        </span>
                      ) : item.status === "PREPARING" || item.status === "IN_PROGRESS" ? (
                        <button
                          onClick={() => handleItemTap(item)}
                          disabled={isLoading}
                          className="px-4 py-2 rounded-lg bg-kds-preparing text-black text-sm font-bold hover:brightness-110 disabled:opacity-50 whitespace-nowrap animate-preparing"
                          title="Segna come pronto — il cameriere sarà notificato"
                        >
                          {isLoading ? "…" : "🔔 PRONTO"}
                        </button>
                      ) : item.status === "READY" ? (
                        <span className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-kds-ready/20 text-kds-ready text-xs font-semibold whitespace-nowrap">
                          <CheckCircle2 size={14} /> Pronto • cameriere avvisato
                        </span>
                      ) : item.status === "SERVED" ? (
                        <span className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-kds-served/20 text-kds-muted text-xs font-semibold whitespace-nowrap">
                          Servito
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-kds-cancelled/20 text-kds-cancelled text-xs font-semibold whitespace-nowrap">
                          {label}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          });
        })()}
      </div>

      {/* Order notes */}
      {order.notes && (
        <div className="mt-3 text-dish-note text-kds-muted bg-kds-card/30 rounded-lg px-3 py-2">
          &#x1F4DD; {order.notes}
        </div>
      )}

      {/* Footer action buttons */}
      {compact && (() => {
        // "Tutta la comanda pronta" appare SOLO se esiste almeno un piatto
        // GIÀ FIRATO ancora in lavorazione. Se i piatti non firati sono
        // ancora "In attesa" lo chef non può ancora lavorarli e quindi non
        // deve nemmeno vedere il pulsante globale di conferma.
        const hasCookingFired = visibleItems.some(
          (i: any) =>
            !!i.firedAt &&
            (i.status === "PREPARING" ||
              i.status === "IN_PROGRESS" ||
              i.status === "PENDING" ||
              i.status === "CONFIRMED"),
        );
        if (!hasCookingFired) return null;
        return (
          <button
            onClick={handleMarkAllReady}
            disabled={loadingAction !== null}
            className="mt-4 w-full flex items-center justify-center gap-2 bg-kds-ready text-white rounded-lg px-4 py-3 text-base font-bold min-h-touch transition-opacity disabled:opacity-50 hover:brightness-110"
            title="Segna tutti i piatti come pronti — il cameriere verrà notificato"
          >
            {loadingAction === "complete" ? (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <CheckCircle2 size={18} />
            )}
            Tutta la comanda pronta
          </button>
        );
      })()}

      {(showFireCourse || showComplete) && (
        <div className="mt-4 flex gap-2">
          {showFireCourse && (
            <button
              onClick={handleFireCourse}
              disabled={loadingAction !== null}
              className={[
                "flex-1 flex items-center justify-center gap-2",
                "bg-kds-preparing text-white rounded-lg px-4 py-3",
                "text-sm font-semibold min-h-touch",
                "transition-opacity disabled:opacity-50",
                loadingAction === "fire" ? "opacity-60 cursor-wait" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {loadingAction === "fire" ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Flame size={16} />
              )}
              Invia {waveLabel(nextWave)}
            </button>
          )}

          {showComplete && (
            <button
              onClick={handleComplete}
              disabled={loadingAction !== null}
              className={[
                "flex-1 flex items-center justify-center gap-2",
                "bg-kds-ready text-white rounded-lg px-4 py-3",
                "text-sm font-semibold min-h-touch",
                "transition-opacity disabled:opacity-50",
                loadingAction === "complete" ? "opacity-60 cursor-wait" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {loadingAction === "complete" ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <CheckCircle2 size={16} />
              )}
              Completa
            </button>
          )}
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────
          Modal conferma ANNULLAMENTO ordine.
          Sostituisce window.confirm() per evitare che lo chef interpreti la
          X come "fatto/avanti". Il modal e' rosso, full-overlay, ed elenca
          le conseguenze (no scarico magazzino, no fatturazione).
          Il bottone primary (default focus) e' "Torna indietro": la
          conferma di annullamento e' l'azione SECONDARIA, intenzionale.
         ────────────────────────────────────────────────────────────────── */}

      {/* ──────────────────────────────────────────────────────────────────
          Modal dettaglio piatto (2026-05-24 fix #2).
          Bug riportato dall'utente: il tap sulla riga item nel KDS
          cambiava lo status invece di mostrare i dettagli del piatto.
          Ora il <div> esterno apre QUESTO modal; il bottone "▶ Inizia" /
          "🔔 PRONTO" resta isolato in `onClick={(e) => e.stopPropagation()}`
          + chiama `handleItemTap` per cambiare status.
         ────────────────────────────────────────────────────────────────── */}
      {detailItem && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="item-detail-title"
          onClick={(e) => {
            // Click sul backdrop = chiudi
            if (e.target === e.currentTarget) setDetailItem(null);
          }}
        >
          <div
            className="bg-kds-card border-2 border-kds-muted/40 rounded-xl max-w-md w-full shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-kds-bg/80 px-5 py-4 flex items-start justify-between gap-3 border-b border-kds-muted/20">
              <div className="flex-1 min-w-0">
                <h2
                  id="item-detail-title"
                  className="text-lg font-bold text-kds-text leading-tight break-words"
                >
                  {detailItem.dish?.name ?? "Piatto"}
                </h2>
                <p className="text-xs text-kds-muted mt-1">
                  Quantità: <b>{detailItem.quantity ?? 1}</b>
                  {" · "}
                  Stato:{" "}
                  <b>{STATUS_LABEL[detailItem.status] ?? detailItem.status}</b>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDetailItem(null)}
                className="text-kds-muted hover:text-kds-text hover:bg-kds-bg rounded-lg p-1.5 transition-colors shrink-0"
                aria-label="Chiudi"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-3 text-sm">
              {detailItem.variant?.name && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-kds-muted mb-1">
                    Variante
                  </div>
                  <div className="text-kds-text font-medium">
                    ◆ {detailItem.variant.name}
                  </div>
                </div>
              )}

              {detailItem.modifications && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-kds-muted mb-1">
                    Modifiche
                  </div>
                  <div className="text-kds-preparing font-medium whitespace-pre-line">
                    {detailItem.modifications}
                  </div>
                </div>
              )}

              {detailItem.notes && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-kds-muted mb-1">
                    Note
                  </div>
                  <div className="text-kds-text whitespace-pre-line">
                    {detailItem.notes}
                  </div>
                </div>
              )}

              {detailItem.dish?.category?.name && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-kds-muted mb-1">
                    Categoria
                  </div>
                  <div className="text-kds-text">
                    {detailItem.dish.category.name}
                  </div>
                </div>
              )}

              {!detailItem.variant?.name &&
                !detailItem.modifications &&
                !detailItem.notes && (
                  <p className="text-kds-muted italic text-center py-4">
                    Nessuna informazione aggiuntiva su questo piatto.
                  </p>
                )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 bg-kds-bg flex justify-end">
              <button
                type="button"
                autoFocus
                onClick={() => setDetailItem(null)}
                className="bg-kds-ready hover:bg-kds-ready/90 text-white rounded-lg px-5 py-2.5 text-sm font-bold transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelConfirmOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-order-title"
          onClick={(e) => {
            // click sul backdrop = chiudi (azione safe)
            if (e.target === e.currentTarget && !cancelInFlight) {
              setCancelConfirmOpen(false);
            }
          }}
        >
          <div
            className="bg-kds-card border-4 border-red-600 rounded-xl max-w-md w-full shadow-2xl shadow-red-600/40 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header rosso pieno */}
            <div className="bg-red-600 text-white px-5 py-4 flex items-center gap-3">
              <AlertTriangle size={32} className="shrink-0" />
              <div>
                <h2
                  id="cancel-order-title"
                  className="text-lg font-extrabold uppercase tracking-wide leading-tight"
                >
                  Annullare l'ordine?
                </h2>
                <p className="text-sm text-red-100 mt-0.5">
                  Ordine #{order.dailyNumber ?? order.id?.slice(0, 6)} ·{" "}
                  {visibleItems.length} piatt
                  {visibleItems.length === 1 ? "o" : "i"}
                </p>
              </div>
            </div>

            {/* Body con elenco conseguenze */}
            <div className="px-5 py-4 text-kds-text">
              <p className="font-semibold mb-3 text-red-300">
                Questa azione è DEFINITIVA.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-red-400 font-bold mt-0.5">✕</span>
                  <span>
                    I piatti <b>NON</b> verranno scaricati dal magazzino
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 font-bold mt-0.5">✕</span>
                  <span>
                    Il tavolo <b>NON</b> verrà fatturato per questi piatti
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 font-bold mt-0.5">✕</span>
                  <span>L'ordine sparirà dalla cucina</span>
                </li>
              </ul>
              <div className="mt-4 bg-yellow-900/40 border-l-4 border-yellow-500 px-3 py-2 text-xs text-yellow-100">
                <b>Per chiudere un piatto pronto premi "Completa", NON la X.</b>
                <br />
                Usa "Annulla" SOLO per ordini sbagliati o fantasma.
              </div>
            </div>

            {/* Footer con due bottoni — primary = SAFE (torna indietro) */}
            <div className="px-5 py-4 bg-kds-bg flex gap-3">
              <button
                type="button"
                autoFocus
                onClick={() => setCancelConfirmOpen(false)}
                disabled={cancelInFlight}
                className="flex-1 bg-kds-ready hover:bg-kds-ready/90 text-white rounded-lg px-4 py-3 text-base font-bold transition-colors disabled:opacity-50"
              >
                ← Torna indietro
              </button>
              <button
                type="button"
                disabled={cancelInFlight}
                onClick={async () => {
                  setCancelInFlight(true);
                  try {
                    await ordersApi.update(order.id, {
                      status: "CANCELLED",
                      cancelReason: "orphan_kitchen",
                    });
                    queryClient.invalidateQueries();
                    setCancelConfirmOpen(false);
                  } catch (err: any) {
                    alert(err?.message || "Cancellazione fallita");
                  } finally {
                    setCancelInFlight(false);
                  }
                }}
                className="bg-red-700 hover:bg-red-600 text-white rounded-lg px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-50 border-2 border-red-500/60"
              >
                {cancelInFlight ? "Annullamento…" : "Sì, annulla ordine"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
